import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface AsanaStatusData {
  connected: boolean;
  asanaWorkspaceId?: string;
  expiresAt?: string;
  isExpired?: boolean;
  createdAt?: string;
}

export interface AsanaAuthorizationData {
  authUrl: string;
}

export interface AsanaProject {
  gid: string;
  name: string;
  resource_type: string;
  created_at: string;
  modified_at: string;
  workspace: {
    gid: string;
    name: string;
  };
}

export class AsanaService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/asana";
  }

  async getAsanaStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: AsanaStatusData; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<AsanaStatusData>>(
        `${this.baseUrl}/status`,
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch Asana connection status.";
      throw new Error(errorMessage);
    }
  }

  async connectAsana(
    config?: AxiosRequestConfig
  ): Promise<{ data: AsanaAuthorizationData; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<AsanaAuthorizationData>
      >(`${this.baseUrl}/connect`, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to connect Asana.";
      throw new Error(errorMessage);
    }
  }

  async disconnectAsana(
    config?: AxiosRequestConfig
  ): Promise<{ data: null; message: string }> {
    try {
      const response = await httpService.post<ApiResponse<null>>(
        `${this.baseUrl}/disconnect`,
        {},
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to disconnect Asana.";
      throw new Error(errorMessage);
    }
  }

  async getAsanaProjects(
    config?: AxiosRequestConfig
  ): Promise<{ data: { projects: AsanaProject[] }; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<{ projects: AsanaProject[] }>
      >(`${this.baseUrl}/projects`, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch Asana projects.";
      throw new Error(errorMessage);
    }
  }
}

export const asanaService = new AsanaService();
